import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import MonthlyCalendarGrid from '@/components/monthly-meal-view/MonthlyCalendarGrid';
import MonthNavigation from '@/components/monthly-meal-view/MonthNavigation';
import { mealPlanService } from '@/services/mealPlan';
import { mealNoteService } from '@/services/mealNoteService';
import { recipeService } from '@/services/recipe';
import { mealCompositionService } from '@/services/mealComposition';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useUserProfile } from '@/hooks/useUserProfile';
import PageLayout from '@/components/layout/PageLayout';

const MonthlyMealView: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const { familyId } = useCurrentUserFamily();
  const { profile } = useUserProfile();

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = format(monthStart, 'yyyy-MM-dd');
  const endDate = format(monthEnd, 'yyyy-MM-dd');

  // Fetch meal plans for the month
  const { data: mealPlans = [], isLoading: isLoadingMealPlans } = useQuery({
    queryKey: ['meal-plans', startDate, endDate],
    queryFn: () => mealPlanService.getMealPlans(monthStart, monthEnd),
    enabled: !!familyId,
  });

  // Fetch meal notes for the month
  const { data: mealNotes = [], isLoading: isLoadingNotes } = useQuery({
    queryKey: ['meal-notes', startDate, endDate],
    queryFn: () => mealNoteService.getMealNotesByDateRange(startDate, endDate),
    enabled: !!familyId,
  });

  // Fetch recipes for dropdown options
  const { data: recipes = [], isLoading: isLoadingRecipes } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => recipeService.getRecipes(),
    enabled: !!familyId,
  });

  // Fetch compositions for dropdown options
  const { data: compositions = [], isLoading: isLoadingCompositions } = useQuery({
    queryKey: ['meal-compositions'],
    queryFn: () => mealCompositionService.getMealCompositions(),
    enabled: !!familyId,
  });

  const isLoading = isLoadingMealPlans || isLoadingNotes || isLoadingRecipes || isLoadingCompositions;

  return (
    <PageLayout
      title="Monthly Meal View"
      description="View and manage your monthly meal plans"
    >
      <div className="space-y-6 pb-14">
        <MonthNavigation
          currentDate={currentDate}
          onDateChange={setCurrentDate}
          userRegistrationDate={profile?.created_at}
        />
        
        <MonthlyCalendarGrid
          currentDate={currentDate}
          mealPlans={mealPlans}
          mealNotes={mealNotes}
          recipes={recipes}
          compositions={compositions}
          isLoading={isLoading}
        />
      </div>
    </PageLayout>
  );
};

export default MonthlyMealView;